const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightweight axios-compatible client built on the pristine fetch reference
// captured in public/index.html before any 3rd-party ad script loads.
// Those ad scripts monkey-patch window.fetch/XMLHttpRequest, which can hang
// axios/fetch calls indefinitely - using the pristine reference avoids that.
const nativeFetch = window.__pristineFetch || window.fetch.bind(window);

const buildUrl = (url, params) => {
  const full = `${API}${url}`;
  if (!params) return full;
  const query = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  ).toString();
  return query ? `${full}?${query}` : full;
};

const request = async (method, url, { data, params, headers } = {}) => {
  const token = localStorage.getItem('admin_token');
  const isFormData = data instanceof FormData;
  const reqHeaders = { ...(headers || {}) };
  if (token) reqHeaders.Authorization = `Bearer ${token}`;
  if (!isFormData && data !== undefined && !reqHeaders['Content-Type']) {
    reqHeaders['Content-Type'] = 'application/json';
  }
  if (isFormData) delete reqHeaders['Content-Type']; // let browser set multipart boundary

  let res;
  const attempts = method === 'GET' ? 2 : 1;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      res = await nativeFetch(buildUrl(url, params), {
        method,
        headers: reqHeaders,
        body: data === undefined ? undefined : (isFormData ? data : JSON.stringify(data)),
      });
      break;
    } catch (networkError) {
      if (attempt === attempts - 1) {
        throw { message: networkError.message, response: null };
      }
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  }

  const contentType = res.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    throw { message: `Request failed with status ${res.status}`, response: { status: res.status, data: body } };
  }
  return { data: body, status: res.status };
};

const api = {
  get: (url, config) => request('GET', url, config),
  post: (url, data, config) => request('POST', url, { ...config, data }),
  put: (url, data, config) => request('PUT', url, { ...config, data }),
  delete: (url, config) => request('DELETE', url, config),
};

// Public APIs
export const getArticles = (params) => api.get('/articles', { params });
export const getBiggestStoryToday = (params) => api.get('/articles/biggest-today', { params });
export const getTopStory = (params) => api.get('/articles/top', { params });
export const getArticle = (id) => api.get(`/articles/${id}`);
export const getTrendingArticles = (category, limit = 6, period = 'week') => {
  const params = { limit, period };
  if (category) params.category = category;
  return api.get('/articles/trending', { params });
};
export const getCategories = () => api.get('/categories');
export const searchArticles = (q) => api.get('/articles/search', { params: { q } });
export const subscribeNewsletter = (data) => api.post('/newsletter/subscribe', data);
export const submitAdInquiry = (data) => api.post('/ad-inquiries', data);

// Admin Auth APIs
export const adminLogin = (password) => api.post('/admin/login', { password });
export const getAdminProfile = () => api.get('/admin/me');

// Admin Article APIs
export const getAdminArticles = () => api.get('/admin/articles');
export const createArticle = (data) => api.post('/admin/articles', data);
export const updateArticle = (id, data) => api.put(`/admin/articles/${id}`, data);
export const deleteArticle = (id) => api.delete(`/admin/articles/${id}`);
export const duplicateArticle = (id) => api.post(`/admin/articles/${id}/duplicate`);
export const reviveArticle = (id) => api.post(`/admin/articles/${id}/revive?minimal=true`);
export const getArticleActivity = (id, days = 30) => api.get(`/admin/articles/${id}/activity`, { params: { days } });
export const cleanMaliciousScripts = () => api.post('/admin/articles/clean-malicious-scripts');
export const bulkDeleteArticles = (ids) => api.post('/admin/articles/bulk-delete', { ids });
export const bulkUpdateArticles = (ids, updates) => api.put('/admin/articles/bulk-update', { ids, updates });

// File Upload
export const uploadFile = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Admin Newsletter APIs
export const getSubscribers = () => api.get('/admin/newsletter');

// Admin Ad Inquiries APIs
export const getAdInquiries = () => api.get('/admin/ad-inquiries');
export const deleteAdInquiry = (id) => api.delete(`/admin/ad-inquiries/${id}`);

// Site Settings APIs (Global Ad Codes)
export const getSiteSettings = () => api.get('/settings');
export const getAdminSettings = () => api.get('/admin/settings');
export const updateSiteSettings = (data) => api.put('/admin/settings', data);

// Page View Counter
export const getPageViews = () => api.get('/pageviews');
export const incrementPageViews = (page = 'homepage') => api.post('/pageviews/increment', { page });
export const getDailyViews = () => api.get('/admin/daily-views');
export const getDailyViewsDetail = (date) => api.get(`/admin/daily-views/${date}`);

export default api;
